import { ChartBinSize } from '@frontend/chart/types';

/** Max inclusive range length per bin_size: hour 60d, day 1y, month/full_period 10y. */
export const BIN_SIZE_MAX_RANGE_MS: Record<ChartBinSize, number> = {
    [ChartBinSize.HOUR]: 60 * 24 * 60 * 60 * 1000,
    [ChartBinSize.DAY]: 365 * 24 * 60 * 60 * 1000,
    [ChartBinSize.MONTH]: 10 * 365 * 24 * 60 * 60 * 1000,
    [ChartBinSize.FULL_PERIOD]: 10 * 365 * 24 * 60 * 60 * 1000
};

/** Coarseness order: finer → coarser (auto-upgrade walks this list). */
export const BIN_SIZE_COARSENESS: ChartBinSize[] = [ChartBinSize.HOUR, ChartBinSize.DAY, ChartBinSize.MONTH, ChartBinSize.FULL_PERIOD];

export const rangeLengthMs = (after: string, before: string): number => {
    const start = Date.parse(after);
    const end = Date.parse(before);
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
        return 0;
    }
    return end - start;
};

export const isBinSizeAllowedForRange = (binSize: ChartBinSize, after: string, before: string): boolean =>
    rangeLengthMs(after, before) <= BIN_SIZE_MAX_RANGE_MS[binSize];

/** Smallest (finest) bin that can cover the range, preferring `preferred` when still valid. */
export const resolveBinSizeForRange = (after: string, before: string, preferred: ChartBinSize, allowed: ChartBinSize[] = BIN_SIZE_COARSENESS): ChartBinSize => {
    const length = rangeLengthMs(after, before);
    const candidates = BIN_SIZE_COARSENESS.filter((bin) => allowed.includes(bin));
    if (candidates.length === 0) {
        return preferred;
    }

    if (candidates.includes(preferred) && length <= BIN_SIZE_MAX_RANGE_MS[preferred]) {
        return preferred;
    }

    for (const bin of candidates) {
        if (length <= BIN_SIZE_MAX_RANGE_MS[bin]) {
            return bin;
        }
    }

    return candidates[candidates.length - 1];
};

/** Clamp `before - after` to the max for `binSize` by moving `after` forward. */
export const clampRangeToBinSize = (after: string, before: string, binSize: ChartBinSize): { after: string; before: string } => {
    const maxMs = BIN_SIZE_MAX_RANGE_MS[binSize];
    const end = new Date(before);
    const start = new Date(after);
    if (Number.isNaN(end.getTime()) || Number.isNaN(start.getTime())) {
        return { after, before };
    }
    if (end.getTime() - start.getTime() <= maxMs) {
        return { after: start.toISOString(), before: end.toISOString() };
    }
    return {
        after: new Date(end.getTime() - maxMs).toISOString(),
        before: end.toISOString()
    };
};

export interface GuardedInsightFilters {
    after: string;
    before: string;
    binSize: ChartBinSize;
    /** True when bin_size was auto-upgraded because the range was too long. */
    binSizeUpgraded: boolean;
    /** True when the range was clamped to fit a locked finer bin. */
    rangeClamped: boolean;
}

/**
 * Enforce bin_size ↔ time-range compatibility.
 * - Unlocked: auto-upgrade bin when range is too long for the preferred bin.
 * - Locked: clamp range to the max for the locked bin (URL force-select).
 */
export const applyBinSizeRangeGuards = (args: {
    after: string;
    before: string;
    preferredBinSize: ChartBinSize;
    allowedBinSizes: ChartBinSize[];
    lockBinSize: boolean;
}): GuardedInsightFilters => {
    const { after, before, preferredBinSize, allowedBinSizes, lockBinSize } = args;
    const allowed = allowedBinSizes.length > 0 ? allowedBinSizes : [preferredBinSize];

    if (lockBinSize) {
        const locked = allowed.includes(preferredBinSize) ? preferredBinSize : allowed[0];
        const clamped = clampRangeToBinSize(after, before, locked);
        return {
            after: clamped.after,
            before: clamped.before,
            binSize: locked,
            binSizeUpgraded: false,
            rangeClamped: clamped.after !== after || clamped.before !== before
        };
    }

    const resolved = resolveBinSizeForRange(after, before, preferredBinSize, allowed);
    const clamped = clampRangeToBinSize(after, before, resolved);

    return {
        after: clamped.after,
        before: clamped.before,
        binSize: resolved,
        binSizeUpgraded: resolved !== preferredBinSize,
        rangeClamped: clamped.after !== after || clamped.before !== before
    };
};
